/**
 * Morse — das Ziel der Stufe, sichtbar. (16.10.2026)
 *
 * Martin, 21:26: *„seit ca. 200 Mal vier Buchstaben im Modus Hören geübt … die Anzeige (Stufe 3, sicher 75 %) ändert
 * sich nicht.“* Die Seite zeigte nur das Mittel der Güte aller Zeichen („sicher“). Der Aufstieg rechnet aber anders
 * (shared/morse-koch.ts, `kochStufeNach` über `kochFenster`): die letzten Aufgaben dieser Stufe mit dem neuen Zeichen
 * — mindestens vier, bei kurzen Aufgaben so viele, bis 100 Zeichen beisammen sind (höchstens 20) —, mindestens 50
 * Zeichen, 90 % richtig, und das neue Zeichen mindestens fünfmal, auch zu 90 %. Hier stehen genau diese vier
 * Bedingungen, was gerade fehlt, und das schwächste Zeichen.
 *
 * Befund dazu (sein Konto, nur Zahlen): seit dem 13.10. übt er „Augen zu“. Dort ist EINE Aufgabe fünf Buchstaben —
 * fest vier Aufgaben waren höchstens 20 von 50 Zeichen, die Regel nie erfüllbar. Seit `kochFenster` (shared/morse-koch.ts)
 * zählt das Fenster zurück, bis 100 Zeichen beisammen sind; `zuKurz` meldet nur noch, wenn selbst zwanzig Aufgaben
 * keine 50 Zeichen ergeben.
 *
 * ⚠️ Ohne `fenster` im Stand (älterer Server) gibt es kein Ziel — die Seite zeigt dann, was sie bisher zeigte.
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-stufenziel…`).
 */
import { KOCH_FENSTER_AUFGABEN, KOCH_FENSTER_HOECHSTENS, KOCH_FENSTER_ZEICHEN, KOCH_LETZTE_STUFE, kochStufe, kochStufenZiel, kochZeichen, type StufenZiel } from "../../shared/morse-koch.js";
import { zielLuecke, zielPunkte } from "../../shared/morse-naechstes.js";
import { stufenLeiste, zielAnteile, type ZielAnteil } from "./morse-balken.js";
import { el, prozent, type MorseStandDaten } from "./morse-teile.js";

export interface ZielAnsicht {
  ziel: StufenZiel;
  /** Was gerade fehlt, als Satz (Großbuchstabe am Anfang). */
  luecke: string;
  /** Das Zeichen der Stufe mit der kleinsten Güte (nur gehörte) — `null`, solange keines gehört ist. */
  schwach: { zeichen: string; guete: number } | null;
  /**
   * Selbst das größte Fenster (`KOCH_FENSTER_HOECHSTENS` Aufgaben) hat zu wenig Zeichen — nur dann ist „genug gehört“
   * mit diesen Aufgaben nicht erreichbar (seit `kochFenster` praktisch nie; vorher bei „Augen zu“ immer).
   */
  zuKurz: boolean;
}

/** Das Ziel für diesen Stand — `null` ohne Fenster (älterer Server) und auf der letzten Stufe. */
export function zielAnsicht(s: MorseStandDaten): ZielAnsicht | null {
  if (!Array.isArray(s.fenster)) return null;
  const st = kochStufe(s.stufe);
  if (st >= KOCH_LETZTE_STUFE) return null;
  const ziel = kochStufenZiel(st, s.fenster);
  const je = new Map(s.zeichen.map((z) => [z.zeichen, z]));
  let schwach: ZielAnsicht["schwach"] = null;
  for (const z of kochZeichen(st)) {
    const d = je.get(z);
    if (!d || d.gehoert <= 0) continue;
    if (!schwach || d.guete < schwach.guete) schwach = { zeichen: z, guete: d.guete };
  }
  const zuKurz = ziel.fehlt === "zeichen" && ziel.aufgaben >= KOCH_FENSTER_HOECHSTENS;
  const satz = zielLuecke(ziel, prozent);
  return { ziel, luecke: satz.charAt(0).toLocaleUpperCase("de-DE") + satz.slice(1), schwach, zuKurz };
}

/** „67 %“ aus ganzen Prozent — mit festem Leerzeichen wie `prozent` (morse-teile.ts). */
const prozentText = (n: number): string => `${n}\u00a0%`;

/**
 * Die Stufenleiste (23.10.2026): ein Strich je Stufe der Koch-Reihe — erreichte voll, die jetzige hoch und im Akzent,
 * offene als leiser Rahmen. Form statt nur Farbe: die jetzige ist doppelt so hoch. Für Vorleser ein `progressbar` mit
 * „Stufe 3 von 40“. `null` ohne brauchbare Stufe.
 */
export function baueStufenLeiste(stufe: number): HTMLElement | null {
  const l = stufenLeiste(stufe, KOCH_LETZTE_STUFE);
  if (!l) return null;
  const leiste = el("div", "morse-stufenleiste");
  leiste.setAttribute("role", "progressbar");
  leiste.setAttribute("aria-label", "Fortschritt durch die Stufen");
  leiste.setAttribute("aria-valuemin", String(l.erste));
  leiste.setAttribute("aria-valuemax", String(l.letzte));
  leiste.setAttribute("aria-valuenow", String(l.stufe));
  leiste.setAttribute("aria-valuetext", l.text);
  for (const art of l.striche) {
    const s = el("span", "morse-stufenleiste-strich");
    s.dataset.art = art;
    leiste.appendChild(s);
  }
  return leiste;
}

/**
 * „2/4 erfüllt“ — die Zahl des Ziels über den vier Balken (vorher rechts in der Stand-Zeile, mit Mini-Balken). Bewusst
 * ohne Gesamtprozent (Prüfer 23.10.2026): neben „Insgesamt 85 % richtig“ läse sich jede zweite Prozentzahl als Quote.
 */
export function zielZahl(a: ZielAnsicht): HTMLElement {
  const p = el("p", "morse-stand-sicher morse-stand-ziel");
  const wert = el("span", "morse-stand-sicher-wert", `${a.ziel.erfuellt}/4`);
  const wort = el("span", "morse-stand-sicher-wort", "erfüllt");
  for (const x of [wert, wort]) x.setAttribute("aria-hidden", "true");
  // Vorleser hören den ganzen Satz (ein aria-label an einem <p> lesen nicht alle vor).
  p.append(wert, wort, el("span", "morse-nur-vorlesen", `Ziel Stufe ${a.ziel.stufe + 1}: ${a.ziel.erfuellt} von 4 Bedingungen erfüllt.`));
  return p;
}

/**
 * Der Balken einer Bedingung (morse-balken.ts `ZielAnteil`): ein <progress> mit `value` — bei einer Quote die echte
 * Trefferquote, dazu die Schwelle als Marke (`data-marke`, die Lage steht in style.css; kein style-Attribut).
 */
function skala(p: ZielAnteil | undefined): HTMLElement {
  const h = el("span", "morse-stufenziel-skala");
  h.setAttribute("aria-hidden", "true");
  const b = el("progress", "morse-stufenziel-balken");
  b.max = 100;
  b.value = p?.balken ?? 0;
  h.appendChild(b);
  if (p?.marke !== null && p?.marke !== undefined) {
    const m = el("span", "morse-stufenziel-marke");
    m.dataset.marke = String(p.marke);
    h.appendChild(m);
  }
  return h;
}

/**
 * Das Ziel der Stufe (neu geordnet 23.10.2026): oben „Bis Stufe 4“ mit „2/4 erfüllt“, darunter IMMER sichtbar die
 * vier Bedingungen als kompakte Leiste, darunter eingeklappt der Satz, was fehlt. Aufgeklappt: die vier Bedingungen mit
 * Namen, Wert und Balken — Zähl-Bedingungen mit Prozent, Quoten mit der Schwelle als Marke (am Telefon 2 × 2, am Rechner in einer Reihe; Form ✓ / Ring, nie nur Farbe), was gezählt
 * wird, das schwächste Zeichen und — wenn es zutrifft — der Hinweis zu „Augen zu“. `offen`: war schon aufgeklappt (bleibt es
 * beim Neuzeichnen).
 */
export function baueZiel(a: ZielAnsicht, offen = false): HTMLElement {
  const z = a.ziel;
  const anteile = zielAnteile(z);
  const wurzel = el("div", "morse-stufenziel-block");
  const kopfZeile = el("div", "morse-stufenziel-zeile");
  kopfZeile.append(el("p", "morse-stufenziel-titel", `Bis Stufe ${z.stufe + 1}`), zielZahl(a));

  // Kompakt und immer sichtbar: vier Balken nebeneinander, je Bedingung einer (Haken = erfüllt). Die Worte dazu stehen
  // aufgeklappt in der Liste — Vorleser bekommen sie dort und in der Zahl oben, die Leiste selbst ist Bild.
  const leiste = el("div", "morse-zielleiste");
  leiste.setAttribute("aria-hidden", "true");
  for (const p of anteile?.punkte ?? []) {
    const teil = el("span", "morse-zielleiste-teil");
    teil.dataset.ok = String(p.ok);
    if (p.art === z.fehlt) teil.dataset.jetzt = "true";
    teil.appendChild(skala(p));
    leiste.appendChild(teil);
  }

  const liste = el("ul", "morse-stufenziel-liste");
  liste.setAttribute("aria-label", `Bedingungen für Stufe ${z.stufe + 1}`);
  zielPunkte(z, prozent).forEach((p, i) => {
    const teil = anteile?.punkte[i];
    // Prozent nur bei Zähl-Bedingungen („80 von 50 Zeichen“); eine Quote nennt ihren Wert schon („85 % von 90 %“).
    const pr = teil?.prozent ?? null;
    const li = el("li", "morse-stufenziel-punkt");
    li.dataset.ok = String(p.ok);
    li.dataset.art = p.art;
    li.dataset.form = teil?.form ?? "zahl";
    if (p.art === z.fehlt) li.dataset.jetzt = "true";
    const form = el("span", "morse-stufenziel-form");
    form.setAttribute("aria-hidden", "true");
    li.append(form, el("span", "morse-stufenziel-name", p.name));
    if (pr !== null) {
      const zahl = el("span", "morse-stufenziel-prozent", prozentText(pr));
      zahl.setAttribute("aria-hidden", "true");
      li.appendChild(zahl);
    }
    li.append(
      skala(teil),
      el("span", "morse-stufenziel-wert", p.wert),
      el("span", "morse-nur-vorlesen", p.ok ? " — erfüllt" : pr !== null ? ` — ${prozentText(pr)}, fehlt noch` : " — fehlt noch"),
    );
    liste.appendChild(li);
  });

  const d = el("details", "morse-stufenziel");
  d.open = offen;
  d.dataset.fehlt = z.fehlt ?? "nichts";
  const kopf = el("summary", "morse-stufenziel-kopf");
  kopf.append(el("span", "morse-stufenziel-luecke", a.luecke));
  d.appendChild(kopf);

  const inhalt = el("div", "morse-stufenziel-inhalt");
  inhalt.appendChild(liste);
  inhalt.appendChild(
    el(
      "p",
      "morse-hinweis",
      `Gezählt werden die letzten Hör-Aufgaben dieser Stufe, in denen ${z.neu} vorkommt — mindestens ${KOCH_FENSTER_AUFGABEN} und so viele, bis ${KOCH_FENSTER_ZEICHEN} Zeichen beisammen sind (höchstens ${KOCH_FENSTER_HOECHSTENS}); entschieden wird ab ${z.mindestZeichen}. Jetzt: ${z.aufgaben} Aufgaben.${z.danach ? ` Danach kommt ${z.danach} dazu.` : ""}`,
    ),
  );
  if (a.schwach) inhalt.appendChild(el("p", "morse-stufenziel-schwach", `Am schwächsten: ${a.schwach.zeichen} (${prozent(a.schwach.guete)} sicher).`));
  if (a.zuKurz) {
    const h = el(
      "p",
      "morse-meldung",
      `Auch die letzten ${KOCH_FENSTER_HOECHSTENS} gezählten Aufgaben ergeben nur ${z.zeichen} von ${z.mindestZeichen} Zeichen — es waren viele kurze dabei. Volle Runden zählen schneller.`,
    );
    h.dataset.art = "hinweis";
    inhalt.appendChild(h);
  }
  d.appendChild(inhalt);
  wurzel.append(kopfZeile, leiste, d);
  return wurzel;
}
